import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, Link,
  InputAdornment, IconButton, Alert, CircularProgress,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility, VisibilityOff,
  CheckCircleOutlined as CheckCircleIcon,
  LockResetOutlined as LockResetIcon,
  BoltOutlined as BoltIcon,
  ErrorOutlineOutlined as ErrorIcon,
} from '@mui/icons-material';
import axios from 'axios';

const MarketingPanel = ({ headline, body }) => (
  <Box
    sx={{
      flex: 1,
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      justifyContent: 'space-between',
      p: 6,
      position: 'relative',
      overflow: 'hidden',
      background: 'var(--gradient-marketing)',
      color: '#fff',
    }}
  >
    <Box sx={{
      position: 'absolute', inset: 0, opacity: 0.5, pointerEvents: 'none',
      background: `
        radial-gradient(circle at 15% 20%, rgba(255,255,255,0.18) 0%, transparent 40%),
        radial-gradient(circle at 85% 75%, rgba(20,184,166,0.3) 0%, transparent 45%),
        radial-gradient(circle at 50% 110%, rgba(139,92,246,0.4) 0%, transparent 50%)
      `,
    }} />

    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{
        width: 40, height: 40, borderRadius: 2,
        background: 'rgba(255,255,255,0.18)',
        backdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid rgba(255,255,255,0.25)',
      }}>
        <BoltIcon sx={{ color: '#fff', fontSize: 22 }} />
      </Box>
      <Typography sx={{
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 800,
        fontSize: '1.05rem',
        letterSpacing: '-0.02em',
      }}>
        SUPERCHARGEME
      </Typography>
    </Box>

    <Box sx={{ position: 'relative', maxWidth: 480 }}>
      <Typography sx={{
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontSize: { md: '2.5rem', lg: '3rem' },
        fontWeight: 800,
        lineHeight: 1.1,
        letterSpacing: '-0.03em',
        mb: 2,
      }}>
        {headline}
      </Typography>
      <Typography sx={{
        fontSize: '1.05rem',
        opacity: 0.85,
        lineHeight: 1.55,
        fontWeight: 400,
      }}>
        {body}
      </Typography>
    </Box>

    <Typography sx={{ position: 'relative', fontSize: '0.8rem', opacity: 0.65 }}>
      © {new Date().getFullYear()} SuperChargeMe. All rights reserved.
    </Typography>
  </Box>
);

const MobileLogo = () => (
  <Box sx={{
    display: { xs: 'flex', md: 'none' },
    alignItems: 'center', gap: 1.25, mb: 4,
  }}>
    <Box sx={{
      width: 36, height: 36, borderRadius: 1.5,
      background: 'var(--gradient-primary)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <BoltIcon sx={{ color: '#fff', fontSize: 20 }} />
    </Box>
    <Typography sx={{
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 800,
      fontSize: '1rem',
      letterSpacing: '-0.02em',
    }}>
      SUPERCHARGEME
    </Typography>
  </Box>
);

const FormPanel = ({ children }) => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: { xs: 3, sm: 6, md: 8 },
      py: 6,
      bgcolor: 'background.default',
    }}
  >
    <Box sx={{ width: '100%', maxWidth: 420 }}>
      <MobileLogo />
      {children}
    </Box>
  </Box>
);

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid reset link. Please request a new password reset.');
        setValidatingToken(false);
        return;
      }
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';
        const response = await axios.get(`${apiUrl}/api/users/validate-reset-token/${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setUserEmail(response.data.email);
        } else {
          setError(response.data.message || 'Invalid or expired reset token');
        }
      } catch (err) {
        console.error('Token validation error:', err);
        setError(err.response?.data?.message || 'Invalid or expired reset token');
      } finally {
        setValidatingToken(false);
      }
    };
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return setError('Password is required');
    if (password.length < 6) return setError('Password must be at least 6 characters long');
    if (!confirmPassword.trim()) return setError('Please confirm your password');
    if (password !== confirmPassword) return setError('Passwords do not match');

    setLoading(true);
    setError('');
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';
      await axios.post(`${apiUrl}/api/users/reset-password`, { token, password });
      setSuccess(true);
    } catch (err) {
      console.error('Reset password error:', err);
      if (err.response?.data?.message) setError(err.response.data.message);
      else if (err.response?.data?.errors) setError(err.response.data.errors[0].msg);
      else setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while validating token
  if (validatingToken) {
    return (
      <Box sx={{
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        bgcolor: 'background.default',
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography sx={{ color: 'text.secondary' }}>
            Validating reset link...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Success state
  if (success) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
        <MarketingPanel
          headline={<>You're all set.<br/>Back to business.</>}
          body="Your password is updated. Sign in with your new credentials and pick up right where you left off."
        />
        <FormPanel>
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'var(--color-secondary-a10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mb: 3,
          }}>
            <CheckCircleIcon sx={{ color: 'var(--color-secondary)', fontSize: 28 }} />
          </Box>
          <Typography sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontWeight: 700,
            fontSize: '1.75rem',
            letterSpacing: '-0.025em',
            mb: 1,
          }}>
            Password reset
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: '0.95rem' }}>
            Your password has been successfully updated. You can now sign in.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/login')}
            sx={{ py: 1.5, fontSize: '0.95rem', fontWeight: 600, borderRadius: 2 }}
          >
            Sign in now
          </Button>
        </FormPanel>
      </Box>
    );
  }

  // Invalid-token state
  if (!tokenValid) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
        <MarketingPanel
          headline={<>Link expired?<br/>No problem.</>}
          body="Reset links are short-lived for your security. Request a new one and you'll be back on track in under a minute."
        />
        <FormPanel>
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mb: 3,
          }}>
            <ErrorIcon sx={{ color: '#EF4444', fontSize: 28 }} />
          </Box>
          <Typography sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontWeight: 700,
            fontSize: '1.75rem',
            letterSpacing: '-0.025em',
            mb: 1,
          }}>
            Invalid link
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 3, fontSize: '0.95rem' }}>
            {error || 'This reset link may have expired or been used already.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/login')}
              sx={{ py: 1.3, borderRadius: 2, flex: 1 }}
            >
              Back to sign in
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/forgot-password')}
              sx={{ py: 1.3, borderRadius: 2, flex: 1, fontWeight: 600 }}
            >
              Request new link
            </Button>
          </Box>
        </FormPanel>
      </Box>
    );
  }

  // Main form
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
      <MarketingPanel
        headline={<>Secure your<br/>command centre.</>}
        body="Choose a strong password you'll remember. We'll get you signed in and back to your data in seconds."
      />
      <FormPanel>
        <Box sx={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'var(--color-primary-a10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mb: 3,
        }}>
          <LockResetIcon sx={{ color: 'var(--color-primary)', fontSize: 28 }} />
        </Box>

        <Typography sx={{
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontWeight: 700,
          fontSize: '1.75rem',
          letterSpacing: '-0.025em',
          mb: 1,
        }}>
          Set new password
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: '0.95rem' }}>
          For <Box component="strong" sx={{ color: 'text.primary' }}>{userEmail}</Box>
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            required fullWidth
            name="password" label="New password" id="new-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            required fullWidth
            name="confirmPassword" label="Confirm new password" id="confirm-new-password"
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    size="small"
                  >
                    {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              py: 1.5,
              fontSize: '0.95rem',
              fontWeight: 600,
              borderRadius: 2,
              position: 'relative',
            }}
          >
            {loading ? (
              <CircularProgress
                size={22}
                sx={{
                  color: 'white',
                  position: 'absolute',
                  top: '50%', left: '50%',
                  marginTop: '-11px', marginLeft: '-11px',
                }}
              />
            ) : 'Reset password'}
          </Button>

          <Typography sx={{
            textAlign: 'center',
            fontSize: '0.875rem',
            color: 'text.secondary',
            mt: 4,
          }}>
            Remembered it?{' '}
            <Link
              component={RouterLink}
              to="/login"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Sign in
            </Link>
          </Typography>
        </Box>
      </FormPanel>
    </Box>
  );
};

export default ResetPassword;
